import { computed, reactive, watch } from 'vue';
import { defineStore } from 'pinia';
import { SetupStoreId } from '@/store/plugins';
import { localStg } from '@/utils/storage';
import { themeSettings as defaultThemeSettings } from '@/theme';

// 定义CSS变量类型
interface CSSVars {
  [key: string]: string;
}

/**
 * 辅助函数：将十六进制颜色转换为RGB数值字符串
 * @param hex 十六进制颜色值
 * @returns RGB格式的颜色值字符串（如："255, 255, 255"）
 */
function hexToRgb(hex: string): string {
  if (typeof hex !== 'string') {
    console.error('颜色值必须是字符串:', hex);
    return '0, 0, 0'; // 返回黑色作为默认值
  }
  
  // 去除可能存在的#前缀
  hex = hex.replace(/^#/, '');
  
  // 处理简写形式（如#fff -> #ffffff）
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }
  
  // 转换为RGB值
  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  
  return `${r}, ${g}, ${b}`;
}

/**
 * 辅助函数：将RGB颜色值调亮或调暗
 * @param hex 十六进制颜色值
 * @param amount 调整量，正数变亮，负数变暗，范围[-100, 100]
 * @returns 调整后的十六进制颜色
 */
function adjustColor(hex: string, amount: number): string {
  if (typeof hex !== 'string') {
    return '#000000';
  }
  
  // 确保amount在合理范围内
  amount = Math.max(-100, Math.min(100, amount));
  
  // 去除#前缀并扩展简写形式
  hex = hex.replace(/^#/, '');
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }

  // 计算新的RGB值
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  // 正数变亮，负数变暗
  if (amount >= 0) {
    r = Math.min(255, Math.floor(r + (255 - r) * (amount / 100)));
    g = Math.min(255, Math.floor(g + (255 - g) * (amount / 100)));
    b = Math.min(255, Math.floor(b + (255 - b) * (amount / 100)));
  } else {
    r = Math.max(0, Math.floor(r + r * (amount / 100)));
    g = Math.max(0, Math.floor(g + g * (amount / 100)));
    b = Math.max(0, Math.floor(b + b * (amount / 100)));
  }

  // 转回十六进制
  const rHex = r.toString(16).padStart(2, '0');
  const gHex = g.toString(16).padStart(2, '0');
  const bHex = b.toString(16).padStart(2, '0');

  return `#${rHex}${gHex}${bHex}`;
}

/**
 * 根据基础颜色生成完整色阶
 * @param baseColor 基础颜色（十六进制）
 * @returns 包含不同色阶的颜色对象
 */
function generateColorScale(baseColor: string): Record<string, string> {
  return {
    '50': adjustColor(baseColor, 95),
    '100': adjustColor(baseColor, 90),
    '200': adjustColor(baseColor, 75),
    '300': adjustColor(baseColor, 55),
    '400': adjustColor(baseColor, 25),
    '500': baseColor,
    '600': adjustColor(baseColor, -20),
    '700': adjustColor(baseColor, -40),
    '800': adjustColor(baseColor, -60),
    '900': adjustColor(baseColor, -80),
    '950': adjustColor(baseColor, -90),
    'light': adjustColor(baseColor, 40),
    'dark': adjustColor(baseColor, -35)
  };
}

/**
 * 生成主题色调色板
 * @param baseColor 主题基础色（十六进制）
 * @returns 包含主题色各色阶的CSS变量对象
 */
function generateColorPalette(baseColor: unknown): Record<string, string> {
  // 确保baseColor是字符串
  const safeColor = typeof baseColor === 'string' ? baseColor : '#000000';
  
  const colorScale = generateColorScale(safeColor);
  
  return {
    '--primary-color': hexToRgb(safeColor),
    '--primary-50-color': hexToRgb(colorScale['50']),
    '--primary-100-color': hexToRgb(colorScale['100']),
    '--primary-200-color': hexToRgb(colorScale['200']),
    '--primary-300-color': hexToRgb(colorScale['300']),
    '--primary-400-color': hexToRgb(colorScale['400']),
    '--primary-500-color': hexToRgb(safeColor),
    '--primary-600-color': hexToRgb(colorScale['600']),
    '--primary-700-color': hexToRgb(colorScale['700']),
    '--primary-800-color': hexToRgb(colorScale['800']),
    '--primary-900-color': hexToRgb(colorScale['900']),
    '--primary-950-color': hexToRgb(colorScale['950']),
    '--primary-light-color': hexToRgb(colorScale['light']),
    '--primary-dark-color': hexToRgb(colorScale['dark']),
  };
}

/**
 * 生成功能色变量及其色阶
 * @param info 信息色（十六进制）
 * @param success 成功色（十六进制）
 * @param warning 警告色（十六进制）
 * @param error 错误色（十六进制）
 * @returns 包含功能色各色阶的CSS变量对象
 */
function generateFunctionalColors(
  info: unknown = '#909399',
  success: unknown = '#67C23A',
  warning: unknown = '#E6A23C',
  error: unknown = '#F56C6C'
): Record<string, string> {
  // 确保所有颜色值都是字符串
  const safeInfo = typeof info === 'string' ? info : '#909399';
  const safeSuccess = typeof success === 'string' ? success : '#67C23A';
  const safeWarning = typeof warning === 'string' ? warning : '#E6A23C';
  const safeError = typeof error === 'string' ? error : '#F56C6C';
  
  // 生成各功能色的色阶
  const infoScale = generateColorScale(safeInfo);
  const successScale = generateColorScale(safeSuccess);
  const warningScale = generateColorScale(safeWarning);
  const errorScale = generateColorScale(safeError);
  
  return {
    '--info-color': hexToRgb(safeInfo),
    '--success-color': hexToRgb(safeSuccess),
    '--warning-color': hexToRgb(safeWarning),
    '--error-color': hexToRgb(safeError),
    
    // 信息色色阶
    '--info-50-color': hexToRgb(infoScale['50']),
    '--info-100-color': hexToRgb(infoScale['100']),
    '--info-200-color': hexToRgb(infoScale['200']),
    '--info-300-color': hexToRgb(infoScale['300']),
    '--info-400-color': hexToRgb(infoScale['400']),
    '--info-500-color': hexToRgb(safeInfo),
    '--info-600-color': hexToRgb(infoScale['600']),
    '--info-700-color': hexToRgb(infoScale['700']),
    '--info-800-color': hexToRgb(infoScale['800']),
    '--info-900-color': hexToRgb(infoScale['900']),
    
    // 成功色色阶
    '--success-50-color': hexToRgb(successScale['50']),
    '--success-100-color': hexToRgb(successScale['100']),
    '--success-200-color': hexToRgb(successScale['200']),
    '--success-300-color': hexToRgb(successScale['300']),
    '--success-400-color': hexToRgb(successScale['400']),
    '--success-500-color': hexToRgb(safeSuccess),
    '--success-600-color': hexToRgb(successScale['600']),
    '--success-700-color': hexToRgb(successScale['700']),
    '--success-800-color': hexToRgb(successScale['800']),
    '--success-900-color': hexToRgb(successScale['900']),
    
    // 警告色色阶
    '--warning-50-color': hexToRgb(warningScale['50']),
    '--warning-100-color': hexToRgb(warningScale['100']),
    '--warning-200-color': hexToRgb(warningScale['200']),
    '--warning-300-color': hexToRgb(warningScale['300']),
    '--warning-400-color': hexToRgb(warningScale['400']),
    '--warning-500-color': hexToRgb(safeWarning),
    '--warning-600-color': hexToRgb(warningScale['600']),
    '--warning-700-color': hexToRgb(warningScale['700']),
    '--warning-800-color': hexToRgb(warningScale['800']),
    '--warning-900-color': hexToRgb(warningScale['900']),
    
    // 错误色色阶
    '--error-50-color': hexToRgb(errorScale['50']),
    '--error-100-color': hexToRgb(errorScale['100']),
    '--error-200-color': hexToRgb(errorScale['200']),
    '--error-300-color': hexToRgb(errorScale['300']),
    '--error-400-color': hexToRgb(errorScale['400']),
    '--error-500-color': hexToRgb(safeError),
    '--error-600-color': hexToRgb(errorScale['600']),
    '--error-700-color': hexToRgb(errorScale['700']),
    '--error-800-color': hexToRgb(errorScale['800']),
    '--error-900-color': hexToRgb(errorScale['900']),
  };
}

// 生成背景色和文本色变量
function generateBackgroundTextColors(isDark: boolean): Record<string, string> {
  if (isDark) {
    return {
      '--layout-bg-color': '28, 28, 28',
      '--container-bg-color': '36, 36, 36',
      '--inverted-bg-color': '28, 28, 28',
      '--base-text-color': '255, 255, 255',
    };
  }
  
  return {
    '--layout-bg-color': '244, 247, 250',
    '--container-bg-color': '255, 255, 255',
    '--inverted-bg-color': '48, 65, 86',
    '--base-text-color': '48, 49, 51',
  };
}

// 生成阴影变量
function generateShadowVars(isDark: boolean): Record<string, string> {
  if (isDark) {
    return {
      '--header-box-shadow': '0 1px 4px rgba(0, 0, 0, 0.45)',
      '--sider-box-shadow': '2px 0 8px 0 rgba(0, 0, 0, 0.25)',
      '--tab-box-shadow': '0 1px 4px rgba(0, 0, 0, 0.45)',
    };
  }
  
  return {
    '--header-box-shadow': '0 1px 4px rgba(0, 21, 41, 0.08)',
    '--sider-box-shadow': '2px 0 8px 0 rgba(29, 35, 41, 0.05)',
    '--tab-box-shadow': '0 1px 4px rgba(0, 21, 41, 0.08)',
  };
}

// 生成其他变量
function generateOtherVars(primaryColor: unknown): Record<string, string> {
  // 确保primaryColor是字符串
  const safeColor = typeof primaryColor === 'string' ? primaryColor : '#409EFF';
  
  return {
    '--nprogress-color': hexToRgb(safeColor),
  };
}

// 安全地获取本地存储值
function safeGetLocalStorage<T>(key: string, defaultValue: T): T {
  const value = localStg.get(key);
  
  // 检查类型是否匹配
  if (
    value !== undefined && 
    value !== null && 
    (typeof defaultValue !== 'object' ? typeof value === typeof defaultValue : true) && 
    (typeof value !== 'object' || Object.keys(value).length > 0)
  ) {
    return value as T;
  }
  
  return defaultValue;
}

export const useThemeStore = defineStore(SetupStoreId.Theme, () => {
  /** 主题相关配置 */
  const theme = reactive({
    /** 主题方案 */
    themeScheme: safeGetLocalStorage<string>('themeScheme', defaultThemeSettings.themeScheme),
    /** 暗黑模式 */
    darkMode: safeGetLocalStorage<boolean>('darkMode', false),
    /** 主题颜色 */
    themeColor: safeGetLocalStorage<string>('themeColor', defaultThemeSettings.themeColor),
    /** 功能色配置 */
    otherColor: defaultThemeSettings.otherColor,
    /** 页面切换动画 */
    pageAnimateMode: safeGetLocalStorage<string>('pageAnimateMode', defaultThemeSettings.page.animateMode),
    /** 是否显示面包屑 */
    showBreadcrumb: safeGetLocalStorage<boolean>('showBreadcrumb', defaultThemeSettings.header.breadcrumb.visible),
    /** 是否显示页脚 */
    showFooter: safeGetLocalStorage<boolean>('showFooter', defaultThemeSettings.footer.visible),
    /** 是否显示页面切换动画 */
    showPageAnimate: safeGetLocalStorage<boolean>('showPageAnimate', defaultThemeSettings.page.animate),
    /** 灰度模式 */
    grayscale: safeGetLocalStorage<boolean>('grayscale', defaultThemeSettings.grayscale),
    /** 色弱模式 */
    colourWeakness: safeGetLocalStorage<boolean>('colourWeakness', defaultThemeSettings.colourWeakness)
  });

  /** css变量对象 */
  const cssVarsRef = computed<CSSVars>(() => {
    // 确保颜色值是字符串类型
    const themeColor = typeof theme.themeColor === 'string' ? theme.themeColor : defaultThemeSettings.themeColor;
    const darkMode = theme.darkMode;
    
    // 安全地获取功能色
    const otherColor = theme.otherColor || defaultThemeSettings.otherColor;
    const info = typeof otherColor.info === 'string' ? otherColor.info : '#909399';
    const success = typeof otherColor.success === 'string' ? otherColor.success : '#67C23A';
    const warning = typeof otherColor.warning === 'string' ? otherColor.warning : '#E6A23C';
    const error = typeof otherColor.error === 'string' ? otherColor.error : '#F56C6C';

    // 设置data-theme属性以支持暗黑模式
    if (darkMode) {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
    }

    // 适用特殊主题模式
    if (theme.grayscale) {
      document.documentElement.classList.add('html-grayscale');
    } else {
      document.documentElement.classList.remove('html-grayscale');
    }

    if (theme.colourWeakness) {
      document.documentElement.classList.add('html-weakness');
    } else {
      document.documentElement.classList.remove('html-weakness');
    }

    // 合并所有CSS变量
    const cssVars: CSSVars = {
      ...generateColorPalette(themeColor),
      ...generateFunctionalColors(info, success, warning, error),
      ...generateBackgroundTextColors(darkMode),
      ...generateShadowVars(darkMode),
      ...generateOtherVars(themeColor)
    };
    
    return cssVars;
  });

  /**
   * 设置暗黑模式
   * @param darkMode 暗黑模式
   */
  function setDarkMode(darkMode: boolean) {
    theme.darkMode = darkMode;
    localStg.set('darkMode', darkMode);
  }

  /**
   * 切换暗黑模式
   */
  function toggleDarkMode() {
    setDarkMode(!theme.darkMode);
  }

  /**
   * 设置主题颜色
   * @param themeColor 主题颜色
   */
  function setThemeColor(themeColor: string) {
    theme.themeColor = themeColor;
    localStg.set('themeColor', themeColor);
  }

  /**
   * 设置功能色
   * @param colorType 颜色类型
   * @param color 颜色值
   */
  function setFunctionalColor(
    colorType: 'info' | 'success' | 'warning' | 'error',
    color: string
  ) {
    theme.otherColor[colorType] = color;
  }

  /**
   * 设置页面切换动画
   * @param mode 切换动画
   */
  function setPageAnimateMode(mode: string) {
    theme.pageAnimateMode = mode;
    localStg.set('pageAnimateMode', mode);
  }

  /**
   * 设置是否显示面包屑
   * @param show 是否显示
   */
  function setShowBreadcrumb(show: boolean) {
    theme.showBreadcrumb = show;
    localStg.set('showBreadcrumb', show);
  }

  /**
   * 设置是否显示页脚
   * @param show 是否显示
   */
  function setShowFooter(show: boolean) {
    theme.showFooter = show;
    localStg.set('showFooter', show);
  }

  /**
   * 设置是否显示页面切换动画
   * @param show 是否显示
   */
  function setShowPageAnimate(show: boolean) {
    theme.showPageAnimate = show;
    localStg.set('showPageAnimate', show);
  }

  /**
   * 设置灰度模式
   * @param grayscale 是否启用灰度模式
   */
  function setGrayscale(grayscale: boolean) {
    theme.grayscale = grayscale;
    localStg.set('grayscale', grayscale);
  }

  /**
   * 设置色弱模式
   * @param colourWeakness 是否启用色弱模式
   */
  function setColourWeakness(colourWeakness: boolean) {
    theme.colourWeakness = colourWeakness;
    localStg.set('colourWeakness', colourWeakness);
  }

  /**
   * 设置主题方案
   * @param scheme 主题方案
   */
  function setThemeScheme(scheme: 'light' | 'dark' | 'auto') {
    theme.themeScheme = scheme;
    localStg.set('themeScheme', scheme);
    
    // 如果设置为dark，自动启用暗黑模式
    if (scheme === 'dark') {
      setDarkMode(true);
    } 
    // 如果设置为light，自动关闭暗黑模式
    else if (scheme === 'light') {
      setDarkMode(false);
    }
    // 如果设置为auto，根据系统偏好设置
    else if (scheme === 'auto') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setDarkMode(prefersDark);
      
      // 监听系统主题变化
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e: MediaQueryListEvent) => {
        setDarkMode(e.matches);
      };
      
      mediaQuery.addEventListener('change', handleChange);
    }
  }

  // 初始化主题方案
  if (theme.themeScheme === 'auto') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (theme.darkMode !== prefersDark) {
      setDarkMode(prefersDark);
    }
  }

  return {
    theme,
    cssVarsRef,
    setDarkMode,
    toggleDarkMode,
    setThemeColor,
    setFunctionalColor,
    setPageAnimateMode,
    setShowBreadcrumb,
    setShowFooter,
    setShowPageAnimate,
    setGrayscale,
    setColourWeakness,
    setThemeScheme
  };
}); 